/**
 * Chart spec returned by the Flow `create_data_chart` tool (`alvie_ai` agent).
 *
 * The tool validates the spec against the rows server-side and answers with a
 * normalized spec in ChartCard vocabulary; the rows stay in the call arguments.
 * Keep this schema in sync with `NormalizedDataChart` in
 * `flow/adk/agents/alvie_ai/tools/data_chart.py`
 * (contract fixture: `__fixtures__/data-chart-tool-call.json`).
 */

import { z } from 'zod'
import { type CopilotChartData } from './types'

export const DATA_CHART_TOOL_NAME = 'create_data_chart'

const SERIES_DIMENSION_TYPES = new Set<string>(['line', 'gradientLine', 'bar', 'area'])

const dataChartMetricSchema = z.object({
  field: z.string().min(1),
  label: z.string(),
  type: z.enum(['number', 'currency', 'percentage'])
})

export const dataChartSpecSchema = z
  .object({
    type: z.enum(['line', 'gradientLine', 'bar', 'area', 'scatter', 'pie', 'donut', 'bar-line', 'line-bar']),
    title: z.string(),
    dimension: z
      .object({
        field: z.string().min(1),
        label: z.string(),
        type: z.enum(['date', 'string'])
      })
      .nullable(),
    series_dimension: z.object({ field: z.string().min(1), label: z.string() }).nullable(),
    metrics: z.array(dataChartMetricSchema).min(1),
    second_metrics: z.array(dataChartMetricSchema),
    stacked: z.boolean(),
    sort: z.enum(['data', 'asc', 'desc', 'metricsAsc', 'metricsDesc']),
    // Same rule as Flow: an invalid code would make Intl.NumberFormat throw while the chart renders.
    currency: z.string().regex(/^([A-Z]{3})?$/)
  })
  .refine(
    (spec) => (spec.type === 'scatter' ? spec.dimension == null && spec.metrics.length === 2 : spec.dimension != null),
    'scatter plots exactly two metrics without a dimension; every other type needs a dimension'
  )
  .refine(
    (spec) => (spec.type === 'bar-line' || spec.type === 'line-bar' ? spec.second_metrics.length > 0 : true),
    'composed charts need second_metrics'
  )
  // Same rule as Flow: series_dimension splits exactly one metric of a cartesian chart (scatter uses it
  // to colour points). A pie or donut cannot draw the split, so ChartCard would sum the series away.
  .refine(
    (spec) =>
      spec.series_dimension == null ||
      spec.type === 'scatter' ||
      (SERIES_DIMENSION_TYPES.has(spec.type) && spec.metrics.length === 1 && spec.second_metrics.length === 0),
    'series_dimension splits exactly one metric of a line, gradientLine, bar or area chart'
  )
  // Same rule as Flow: a date axis is always chronological, so ChartCard has no other order to apply.
  .refine((spec) => spec.dimension?.type !== 'date' || spec.sort === 'data', 'a date dimension only sorts by data')
  .transform(({ series_dimension: seriesDimension, second_metrics: secondMetrics, ...rest }) => ({
    ...rest,
    seriesDimension,
    secondMetrics
  }))

export type DataChartSpec = z.infer<typeof dataChartSpecSchema>
export type DataChartMetric = DataChartSpec['metrics'][number]

export interface CopilotDataChart {
  kind: 'dataChart'
  spec: DataChartSpec
  data: Array<Record<string, unknown>>
  /** AG-UI tool call id — ChartCard card id and markdown chart token key. */
  sourceCallId: string
}

/**
 * Any chart a Copilot message can embed. `CopilotChartData` is the legacy shape
 * (Flow `create_chart`, forecast, causal impact) mapped by the temporary adapter.
 */
export type CopilotChart = CopilotChartData | CopilotDataChart

export const isCopilotDataChart = (value: unknown): value is CopilotDataChart =>
  typeof value === 'object' && value != null && 'kind' in value && value.kind === 'dataChart'
